import { Container, MantineSize, Text, Timeline } from '@mantine/core';
import React, { ReactNode, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Check,
  Checkbox,
  Download,
  FileExport,
  Login,
  Logout,
  Minus,
  Pencil,
  Plus,
  SquareOff,
  UserCheck,
  UserOff,
  Link,
  X,
} from 'tabler-icons-react';
import { AuditLogActions } from '../../../enums/retrieve-audit-logs.enum';
import { formatDateWithTime } from '../../../helpers/date-convert';
import { IPaginationMeta } from '../../../type/admin.types';
import { BasicAuditLog } from '../../../type/retrieve-audit-logs.types';
import '../logs.styles.css';
import { getEntityType } from './logs-details-section-table-rows.component';

const TimelineText = ({
  color,
  size,
  children,
  mt,
}: {
  color?: string;
  size?: MantineSize;
  children: ReactNode;
  mt?: number | string;
}) => {
  return (
    <Text
      style={size === 'xs' ? { fontSize: '12px' } : { fontSize: '14px' }}
      color={color}
      size={size}
      mt={mt}
    >
      {children}
    </Text>
  );
};

interface Props {
  auditLogData: BasicAuditLog[] | undefined;
  setSelectedLog: React.Dispatch<
    React.SetStateAction<BasicAuditLog | undefined>
  >;
  createRequest: (
    e?: any,
    formatStartDate?: string,
    formatEndDate?: string,
    pageToGet?: number,
    username?: string,
  ) => Promise<void>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  paginationData: IPaginationMeta | undefined;
  dateRange: [Date | null, Date | null] | undefined;
  search: string;
}

function LogsTimeline(props: Readonly<Props>) {
  const {
    auditLogData,
    setSelectedLog,
    createRequest,
    setPage,
    page,
    dateRange,
    paginationData,
    search,
  } = props;
  const [active, setActive] = useState<number | undefined>();

  const handleClick = (index: number) => {
    if (auditLogData) {
      setSelectedLog(auditLogData[index]);
      setActive(index);
    }
  };

  const getIcon = (action: AuditLogActions) => {
    const isApproveOnBoarding = action === AuditLogActions.APPROVE_ONBOARDING;
    const isDeclineOnBoarding = action === AuditLogActions.DECLINE_ONBOARDING;
    const isEnableEntity = () => {
      return (
        action === AuditLogActions.ENABLE_ONBOARDING ||
        action === AuditLogActions.ENABLE_ACCOUNT ||
        action === AuditLogActions.ENABLE_ENTITY
      );
    };
    const isDisableEntity = () => {
      return (
        action === AuditLogActions.DISABLE_ONBOARDING ||
        action === AuditLogActions.DISABLE_ACCOUNT ||
        action === AuditLogActions.DISABLE_ENTITY
      );
    };
    const isExportTransactions = action === AuditLogActions.EXPORT_TRANSACTIONS;
    const isUpdateField = () => {
      return (
        action === AuditLogActions.UPDATE_ONBOARDING ||
        action === AuditLogActions.UPDATE_ACCOUNT ||
        action === AuditLogActions.UPDATE_POS ||
        action === AuditLogActions.UPDATE_MID
      );
    };
    const isExportFiles = action === AuditLogActions.EXPORT_FILES;
    const isCreateAction = () => {
      return (
        action === AuditLogActions.CREATE_POS ||
        action === AuditLogActions.ASSIGN_MID ||
        action === AuditLogActions.CREATE_MID
      );
    };
    const isUnassignAction = action === AuditLogActions.UNASSIGN_MID;
    const isEnableMid = action === AuditLogActions.ENABLE_MID;
    const isDisableMid = action === AuditLogActions.DISABLE_MID;
    const isLogin = action === AuditLogActions.LOGIN;
    const isLogout = action === AuditLogActions.LOGOUT;
    const isCreatePaymentLink = action === AuditLogActions.CREATE_PAYMENT_LINK;

    if (isApproveOnBoarding) {
      return <Check size={15} />;
    }
    if (isDeclineOnBoarding) {
      return <X size={15} />;
    }
    if (isEnableEntity()) {
      return <UserCheck size={15} />;
    }
    if (isDisableEntity()) {
      return <UserOff size={15} />;
    }
    if (isExportTransactions) {
      return <Download size={15} />;
    }
    if (isUpdateField()) {
      return <Pencil size={15} />;
    }
    if (isExportFiles) {
      return <FileExport size={15} />;
    }
    if (isCreateAction()) {
      return <Plus size={15} />;
    }
    if (isUnassignAction) {
      return <Minus size={15} />;
    }
    if (isEnableMid) {
      return <Checkbox size={15} />;
    }
    if (isDisableMid) {
      return <SquareOff size={15} />;
    }
    if (isLogin) {
      return <Login />;
    }
    if (isLogout) {
      return <Logout />;
    }
    if (isCreatePaymentLink) {
      return <Link />;
    }
  };

  const handleNext = () => {
    if (
      paginationData?.totalPages !== undefined &&
      page <= paginationData.totalPages
    ) {
      const nextPage = page + 1;
      if (nextPage > paginationData.totalPages) {
        return;
      }
      setPage(nextPage);

      if (dateRange?.[0] && dateRange?.[1]) {
        let startDate = dateRange[0];
        let offset = startDate.getTimezoneOffset();
        startDate = new Date(startDate.getTime() - offset * 60 * 1000);
        const formatStartDate =
          startDate.toISOString().split('T')[0] + ' 00:00:00';

        let endDate = dateRange[1];
        offset = endDate.getTimezoneOffset();
        endDate = new Date(endDate.getTime() - offset * 60 * 1000);
        const formatEndDate = endDate.toISOString().split('T')[0] + ' 23:59:59';

        createRequest(
          undefined,
          formatStartDate,
          formatEndDate,
          nextPage,
          search,
        );
      } else {
        createRequest(undefined, undefined, undefined, nextPage, search);
      }
    }
  };

  if (auditLogData?.length && auditLogData?.length > 0) {
    return (
      <Container fluid>
        <InfiniteScroll
          dataLength={auditLogData ? auditLogData.length : 1} //This is important field to render the next data
          next={() => handleNext()}
          hasMore={
            paginationData?.totalItems &&
            paginationData.itemCount !== 0 &&
            auditLogData
              ? paginationData.totalItems > auditLogData.length
              : false
          }
          loader={
            <Text color="gray" pt={20} size="sm">
              Loading...
            </Text>
          }
          endMessage={
            <Text color="gray" pt={20} size="sm">
              End of logs
            </Text>
          }
        >
          <Timeline bulletSize={24} lineWidth={2} className="timeline">
            {auditLogData?.map((auditLog, index) => {
              const isUpdateEntity = () => {
                return (
                  auditLog.logActionId === AuditLogActions.UPDATE_ONBOARDING ||
                  auditLog.logActionId === AuditLogActions.UPDATE_ACCOUNT ||
                  auditLog.logActionId === AuditLogActions.UPDATE_POS
                );
              };
              const isUpdateMid = () => {
                return (
                  auditLog.logActionId === AuditLogActions.UPDATE_MID ||
                  auditLog.logActionId === AuditLogActions.ENABLE_MID ||
                  auditLog.logActionId === AuditLogActions.DISABLE_MID
                );
              };
              const isExportTransactions = () => {
                return (
                  auditLog.logActionId === AuditLogActions.EXPORT_TRANSACTIONS
                );
              };
              const isExportFiles = () => {
                return auditLog.logActionId === AuditLogActions.EXPORT_FILES;
              };
              const isCreatePos = () => {
                return auditLog.logActionId === AuditLogActions.CREATE_POS;
              };
              const isCreateMid = () => {
                return auditLog.logActionId === AuditLogActions.CREATE_MID;
              };
              const isAssignMid = () => {
                return auditLog.logActionId === AuditLogActions.ASSIGN_MID;
              };
              const isUnassignMid = () => {
                return auditLog.logActionId === AuditLogActions.UNASSIGN_MID;
              };
              const isApproveOnBoarding = () => {
                return (
                  auditLog.logActionId === AuditLogActions.APPROVE_ONBOARDING
                );
              };
              const isDeclineOnBoarding = () => {
                return (
                  auditLog.logActionId === AuditLogActions.DECLINE_ONBOARDING
                );
              };
              const isEnableEntity = () => {
                return (
                  auditLog.logActionId === AuditLogActions.ENABLE_ONBOARDING ||
                  auditLog.logActionId === AuditLogActions.ENABLE_ACCOUNT ||
                  auditLog.logActionId === AuditLogActions.ENABLE_ENTITY
                );
              };
              const isDisableEntity = () => {
                return (
                  auditLog.logActionId === AuditLogActions.DISABLE_ONBOARDING ||
                  auditLog.logActionId === AuditLogActions.DISABLE_ACCOUNT ||
                  auditLog.logActionId === AuditLogActions.DISABLE_ENTITY
                );
              };
              const isLogin = () => {
                return auditLog.logActionId === AuditLogActions.LOGIN;
              };
              const isLogout = () => {
                return auditLog.logActionId === AuditLogActions.LOGOUT;
              };
              const isEmptyAuditLogDetails = () => {
                return auditLog.details.length === 0;
              };
              const isCreatePaymentLink = () => {
                return (
                  auditLog.logActionId === AuditLogActions.CREATE_PAYMENT_LINK
                );
              };

              const hasMultipleAuditDetails = auditLog.details.length > 1;

              const getTextFieldOrFields = () => {
                return hasMultipleAuditDetails ? 'fields' : 'field';
              };

              const getPluralSuffix = () => {
                return getEntityType(auditLog) === 'branch' ? 'es' : 's';
              };

              return (
                <Timeline.Item
                  bullet={getIcon(auditLog.logActionId)}
                  title={AuditLogActions[auditLog.logActionId]}
                  onClick={() => {
                    handleClick(index);
                  }}
                  key={index}
                  active={active === index}
                >
                  {isEmptyAuditLogDetails() && (
                    <TimelineText>Log details is empty</TimelineText>
                  )}
                  {isUpdateEntity() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      updated {auditLog.details.length} {getTextFieldOrFields()}{' '}
                      to{' '}
                      <strong>{auditLog.details[0].targetMerchantName}</strong>
                    </TimelineText>
                  )}
                  {isUpdateMid() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      updated {auditLog.details.length} {getTextFieldOrFields()}
                    </TimelineText>
                  )}
                  {isExportTransactions() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      exported{' '}
                      <strong>{auditLog.details[0].targetUsername}</strong>'s{' '}
                      transactions
                    </TimelineText>
                  )}
                  {isExportFiles() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      exported{' '}
                      <strong>{auditLog.details[0].targetMerchantName}</strong>
                      's files
                    </TimelineText>
                  )}
                  {isCreatePos() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      created a POS for{' '}
                      <strong>
                        {auditLog.details[0].targetMerchantName
                          ? auditLog.details[0].targetMerchantName
                          : auditLog.details[0].targetUsername}
                      </strong>
                    </TimelineText>
                  )}
                  {isCreateMid() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      created MID
                    </TimelineText>
                  )}
                  {isAssignMid() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      assigned MID{' '}
                    </TimelineText>
                  )}
                  {isUnassignMid() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      unassigned MID{' '}
                    </TimelineText>
                  )}
                  {isApproveOnBoarding() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      approved{' '}
                      {hasMultipleAuditDetails ? (
                        <strong>{auditLog.details.length} merchants</strong>
                      ) : (
                        <strong>
                          {auditLog.details[0].targetMerchantName
                            ? auditLog.details[0].targetMerchantName
                            : auditLog.details[0].targetUsername}
                        </strong>
                      )}
                    </TimelineText>
                  )}
                  {isDeclineOnBoarding() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      declined{' '}
                      {hasMultipleAuditDetails ? (
                        <strong>{auditLog.details.length} merchants</strong>
                      ) : (
                        <strong>
                          {auditLog.details[0].targetMerchantName
                            ? auditLog.details[0].targetMerchantName
                            : auditLog.details[0].targetUsername}
                        </strong>
                      )}
                    </TimelineText>
                  )}
                  {isEnableEntity() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      enabled{' '}
                      {hasMultipleAuditDetails ? (
                        <strong>
                          {auditLog.details.length} {getEntityType(auditLog)}
                          {getPluralSuffix()}
                        </strong>
                      ) : (
                        <strong>
                          {auditLog.details[0].targetMerchantName
                            ? auditLog.details[0].targetMerchantName
                            : auditLog.details[0].targetUsername}
                        </strong>
                      )}
                    </TimelineText>
                  )}
                  {isDisableEntity() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      disabled{' '}
                      {hasMultipleAuditDetails ? (
                        <strong>
                          {auditLog.details.length} {getEntityType(auditLog)}
                          {getPluralSuffix()}
                        </strong>
                      ) : (
                        <strong>
                          {auditLog.details[0].targetMerchantName
                            ? auditLog.details[0].targetMerchantName
                            : auditLog.details[0].targetUsername}
                        </strong>
                      )}
                    </TimelineText>
                  )}
                  {isLogin() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      logged in
                    </TimelineText>
                  )}
                  {isLogout() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      logged out
                    </TimelineText>
                  )}
                  {isCreatePaymentLink() && (
                    <TimelineText>
                      <strong>{auditLog.details[0].originUsername}</strong>{' '}
                      created a payment link
                    </TimelineText>
                  )}
                  <TimelineText color="dimmed" size="xs" mt={4}>
                    {formatDateWithTime(auditLog.dateCreated.toString())}
                  </TimelineText>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </InfiniteScroll>
      </Container>
    );
  } else {
    return (
      <Text color="gray" pt={20} size="sm">
        No logs to show
      </Text>
    );
  }
}

export default LogsTimeline;
